import { useNavigate } from 'react-router-dom'
import { CalendarCheck, WarningCircle } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { useSessions, type SessionRow } from '../api/gate'
import { useVillages } from '../api/reference'
import { useScope } from '../api/appConfig'
import { DateFilter, FilterBar, SelectFilter } from '../components/FilterBar'
import { ConsolePage } from '../components/Layout'
import { Check, Code, Kg, Status, When } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'

const LIMIT = 25

/** Collection days — F4.1-m (/collection-days) and F4.11-e. */
export function SessionsPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({
    status: '',
    captureMode: '',
    groupId: '',
    from: '',
    to: '',
    page: 1,
  })
  const villages = useVillages()
  const { v1 } = useScope()
  const q = useSessions({
    ...f,
    status: f.status as never,
    captureMode: f.captureMode as never,
    limit: LIMIT,
  })

  const columns: Column<SessionRow>[] = [
    { id: 'code', header: 'Day', cell: (r) => <Code>{r.code}</Code> },
    { id: 'village', header: 'Village', cell: (r) => r.villageName ?? '—' },
    { id: 'operator', header: 'Operator', cell: (r) => r.operatorName ?? '—', hideBelow: 'md' },
    {
      id: 'mode',
      header: 'Mode',
      cell: (r) => <Status status={r.captureMode === 'PAPER' ? 'PAPER' : 'APP'} />,
      hideBelow: 'lg',
    },
    {
      id: 'lots',
      header: 'Lots',
      align: 'right',
      mono: true,
      cell: (r) => (
        <span title={`${r.lotsMain} good · ${r.lotsConv} ordinary`}>
          {r.lotsMain}
          {r.lotsConv > 0 && <span className="text-muted"> +{r.lotsConv}</span>}
        </span>
      ),
    },
    {
      id: 'net',
      header: 'Net kg',
      align: 'right',
      cell: (r) => <Kg grams={r.netG} />,
      hideBelow: 'sm',
    },
    v1
      ? {
          id: 'records',
          header: 'Records',
          cell: (r: SessionRow) =>
            r.status === 'OPEN' ? (
              <span className="text-[13px] text-muted">open</span>
            ) : r.allCommandsArrived ? (
              <Status status="ACTIVE" label="All sent" />
            ) : (
              <span className="text-[13px] font-semibold text-warn">Still arriving</span>
            ),
          hideBelow: 'md' as const,
        }
      : {
          id: 'balanced',
          header: 'Count',
          cell: (r: SessionRow) =>
            r.status === 'OPEN' ? (
              <span className="text-[13px] text-muted">open</span>
            ) : (
              <span className="flex gap-2" title="tags · seals · bags · commands">
                <Check ok={r.tagsBalanced} />
                <Check ok={r.sealsBalanced} />
                <Check ok={r.bagsBalanced} />
                <Check ok={r.allCommandsArrived} />
              </span>
            ),
          hideBelow: 'md' as const,
        },
    {
      id: 'exceptions',
      header: 'Open exc.',
      align: 'right',
      mono: true,
      cell: (r) =>
        r.openExceptions > 0 ? (
          <span className="font-semibold text-warn">{r.openExceptions}</span>
        ) : (
          <span className="text-muted">0</span>
        ),
      hideBelow: 'sm',
    },
    { id: 'opened', header: 'Opened', cell: (r) => <When at={r.openedAt} />, hideBelow: 'lg' },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <Status status={r.status} />
          {r.otherOperatorOpenSameDay && (
            <WarningCircle
              className="text-warn"
              aria-label="Another operator has a day open in this village"
            />
          )}
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Collection days"
      description={
        v1
          ? 'Every day at the gate by village and operator: open or closed, paper days, and whether every record from the phone has arrived. V1 has no counts or reconcile.'
          : 'Every day at the gate by village and operator: open, closed and reconciled, paper days, and whether tags, seals, bags and commands balanced.'
      }
    >
      <DataTable<SessionRow>
        caption="Collection days"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/sessions/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Status"
              value={f.status as '' | 'OPEN'}
              onChange={(status) => setF({ status })}
              options={[
                { value: 'OPEN', label: 'Open' },
                { value: 'CLOSED', label: 'Closed' },
                ...(v1 ? [] : [{ value: 'RECONCILED' as const, label: 'Reconciled' }]),
              ]}
            />
            <SelectFilter
              label="Mode"
              value={f.captureMode as '' | 'APP'}
              onChange={(captureMode) => setF({ captureMode })}
              options={[
                { value: 'APP', label: 'App day' },
                { value: 'PAPER', label: 'Paper day' },
              ]}
            />
            <SelectFilter
              label="Village"
              value={f.groupId}
              onChange={(groupId) => setF({ groupId })}
              options={(villages.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
            />
            <DateFilter label="From" value={f.from} onChange={(from) => setF({ from })} />
            <DateFilter label="To" value={f.to} onChange={(to) => setF({ to })} />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<CalendarCheck />}
            title="No collection days"
            description="Days appear when an operator opens one on the phone (or a paper day is typed in)."
          />
        }
      />
    </ConsolePage>
  )
}
